// Matches the CSS "tablet" breakpoint (min-width: 768px, see _helpers.scss) so the
// mobile/desktop row+position config and the product grid's own 2-column mobile
// layout always agree on which mode is active.
const MOBILE_BREAKPOINT = 767;

export default class PLPInterstitial {
  constructor(root = document) {
    this.root = root;
    this.resizeTimer = null;
    this.liveResizeFrame = null;
    this.lastMode = null;
    this.lastColumns = null;

    this.handleResize = this.handleResize.bind(this);
    this.handleClick = this.handleClick.bind(this);

    this.init();
  }

  init() {
    this.render();

    window.addEventListener("resize", this.handleResize);
    document.addEventListener("click", this.handleClick);
  }

  destroy() {
    window.removeEventListener("resize", this.handleResize);

    document.removeEventListener("click", this.handleClick);

    clearTimeout(this.resizeTimer);

    if (this.heightSyncFrame) {
      cancelAnimationFrame(this.heightSyncFrame);

      this.heightSyncFrame = null;
    }

    if (this.liveResizeFrame) {
      cancelAnimationFrame(this.liveResizeFrame);

      this.liveResizeFrame = null;
    }
  }

  getGrid() {
    return this.root.querySelector("[data-product-grid]");
  }

  getTemplateContainer() {
    return this.root.querySelector("[data-plp-interstitial-templates]");
  }

  getTemplates() {
    const container = this.getTemplateContainer();

    if (!container) {
      return [];
    }

    return Array.from(container.querySelectorAll("[data-plp-interstitial-template]"));
  }

  isMobile() {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
  }

  getMode() {
    return this.isMobile() ? "mobile" : "desktop";
  }

  getColumnCount() {
    const grid = this.getGrid();

    if (!grid) {
      return 1;
    }

    const styles = window.getComputedStyle(grid);

    const template = styles.gridTemplateColumns;

    if (!template || template === "none") {
      return 1;
    }

    const columns = template.trim().split(/\s+/).filter(Boolean);

    return Math.max(columns.length, 1);
  }

  getVisibleProductCards() {
    const grid = this.getGrid();

    if (!grid) {
      return [];
    }

    return Array.from(grid.querySelectorAll(":scope > [data-product-card]")).filter((card) => {
      return !card.hidden && card.style.display !== "none" && !card.hasAttribute("data-pending-reveal");
    });
  }

  removeRenderedInterstitials() {
    const grid = this.getGrid();

    if (!grid) {
      return;
    }

    grid.querySelectorAll(":scope > [data-plp-interstitial-rendered]").forEach((item) => item.remove());
  }

  getConfig(template) {
    const mobile = this.isMobile();

    const row = Number(mobile ? template.dataset.mobileRow : template.dataset.desktopRow);

    const position = String(mobile ? template.dataset.mobilePosition : template.dataset.desktopPosition)
      .trim()
      .toLowerCase();

    const displayMobile = template.dataset.displayMobile !== "false";

    const columns = this.getColumnCount();

    let width = Number(template.dataset.width) || 1;

    width = Math.max(1, Math.min(width, columns));

    return {
      template,
      row,
      position: position === "right" ? "right" : "left",
      width,
      displayMobile,
    };
  }

  getActiveInterstitials() {
    return this.getTemplates()
      .map((template) => this.getConfig(template))
      .filter((config) => {
        if (!Number.isInteger(config.row) || config.row < 1) {
          console.warn("[PLP Interstitial] Skipped — invalid row for this breakpoint:", {
            id: config.template.dataset.interstitialId,
            mode: this.getMode(),
            rawRow: this.isMobile() ? config.template.dataset.mobileRow : config.template.dataset.desktopRow,
            width: config.width,
          });

          return false;
        }

        if (this.isMobile() && !config.displayMobile) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        if (a.row !== b.row) {
          return a.row - b.row;
        }

        if (a.position === b.position) {
          return 0;
        }

        return a.position === "left" ? -1 : 1;
      });
  }

  findAvailableRange(cells, width, preferredStart, position) {
    const maxStart = cells.length - width;

    const isAvailable = (start) => {
      if (start < 0 || start > maxStart) {
        return false;
      }

      for (let index = start; index < start + width; index += 1) {
        if (cells[index] !== null) {
          return false;
        }
      }

      return true;
    };

    if (isAvailable(preferredStart)) {
      return preferredStart;
    }

    if (position === "right") {
      for (let start = maxStart; start >= 0; start -= 1) {
        if (isAvailable(start)) {
          return start;
        }
      }
    } else {
      for (let start = 0; start <= maxStart; start += 1) {
        if (isAvailable(start)) {
          return start;
        }
      }
    }

    return -1;
  }

  buildRows(products, interstitials) {
    const columns = this.getColumnCount();

    const byRow = new Map();

    interstitials.forEach((config) => {
      if (!byRow.has(config.row)) {
        byRow.set(config.row, []);
      }

      byRow.get(config.row).push(config);
    });

    const highestInterstitialRow = interstitials.length ? Math.max(...interstitials.map((item) => item.row)) : 0;

    const rows = [];

    let productIndex = 0;
    let rowNumber = 1;

    while (productIndex < products.length || rowNumber <= highestInterstitialRow) {
      const cells = Array(columns).fill(null);

      const rowInterstitials = byRow.get(rowNumber) || [];

      rowInterstitials.forEach((config) => {
        const preferredStart = config.position === "right" ? columns - config.width : 0;

        const start = this.findAvailableRange(cells, config.width, preferredStart, config.position);

        if (start === -1) {
          // A row only has `columns` cells to give out (2 on mobile, 3-4 on larger
          // breakpoints). This fires when two interstitials configured for the same
          // row can't both fit at once — most likely on mobile, since a "2 wide"
          // banner alone already fills the entire 2-column mobile row.
          console.warn("[PLP Interstitial] Skipped — row is full at this breakpoint:", {
            id: config.template.dataset.interstitialId,
            mode: this.getMode(),
            row: rowNumber,
            width: config.width,
            columns,
          });

          return;
        }

        cells[start] = {
          type: "interstitial",
          config,
          rowNumber,
        };

        for (let offset = 1; offset < config.width; offset += 1) {
          cells[start + offset] = {
            type: "reserved",
          };
        }
      });

      for (let cellIndex = 0; cellIndex < columns; cellIndex += 1) {
        if (cells[cellIndex]) {
          continue;
        }

        if (productIndex >= products.length) {
          continue;
        }

        cells[cellIndex] = {
          type: "product",
          element: products[productIndex],
          rowNumber,
        };

        productIndex += 1;
      }

      rows.push(cells);
      rowNumber += 1;
    }

    return rows;
  }

  createInterstitial(config, rowNumber) {
    const wrapper = document.createElement("div");

    wrapper.className = "main-collection__interstitial";

    wrapper.dataset.plpInterstitialRendered = "";

    wrapper.dataset.interstitialWidth = String(config.width);

    wrapper.dataset.interstitialRow = String(rowNumber);

    // Lets render() reuse this banner instead of rebuilding it (see findReusableInterstitial).
    wrapper.__plpInterstitialTemplate = config.template;

    wrapper.style.gridColumn = `span ${config.width}`;

    wrapper.appendChild(config.template.content.cloneNode(true));

    const aspectRatio = wrapper.querySelector("[data-aspect-ratio]")?.dataset.aspectRatio;

    if (aspectRatio) {
      wrapper.style.setProperty("--plp-interstitial-fallback-aspect-ratio", aspectRatio);
    }

    return wrapper;
  }

  // An already-rendered banner for the same template, row and width, so a
  // re-render keeps its DOM (image/video) instead of rebuilding it.
  findReusableInterstitial(existing, config, rowNumber) {
    const index = existing.findIndex(
      (banner) =>
        banner.__plpInterstitialTemplate === config.template &&
        banner.dataset.interstitialRow === String(rowNumber) &&
        banner.dataset.interstitialWidth === String(config.width),
    );

    return index === -1 ? null : existing.splice(index, 1)[0];
  }

  /*
   * Runs on every grid update (each background page, grouping verification…),
   * so it must not touch nodes that are already in the right place: moving a
   * product card detaches it, which replays its reveal animation and repaints
   * its image — the PLP "blink". Banners are reused, and only nodes that are
   * actually out of order get moved.
   */
  render() {
    const grid = this.getGrid();

    if (!grid) {
      return;
    }

    const existingBanners = Array.from(grid.querySelectorAll(":scope > [data-plp-interstitial-rendered]"));

    const finish = () => {
      existingBanners.forEach((banner) => banner.remove());

      this.lastMode = this.getMode();
      this.lastColumns = this.getColumnCount();
    };

    if (Number(this.root.currentPage) > 1) {
      finish();

      return;
    }

    const products = this.getVisibleProductCards();

    const interstitials = this.getActiveInterstitials();

    if (!products.length || !interstitials.length) {
      finish();

      return;
    }

    const rows = this.buildRows(products, interstitials);

    const orderedNodes = [];

    rows.forEach((cells) => {
      cells.forEach((cell) => {
        if (!cell || cell.type === "reserved") {
          return;
        }

        if (cell.type === "product") {
          const visualRow = String(cell.rowNumber);

          if (cell.element.dataset.plpVisualRow !== visualRow) {
            cell.element.dataset.plpVisualRow = visualRow;
          }

          orderedNodes.push(cell.element);

          return;
        }

        if (cell.type === "interstitial") {
          orderedNodes.push(
            this.findReusableInterstitial(existingBanners, cell.config, cell.rowNumber) ||
              this.createInterstitial(cell.config, cell.rowNumber),
          );
        }
      });
    });

    // Banners no longer in the layout.
    existingBanners.forEach((banner) => banner.remove());

    // Start of the visible run: the first visible product or kept banner.
    const orderedSet = new Set(orderedNodes);

    let cursor = Array.from(grid.children).find((child) => orderedSet.has(child)) || null;

    orderedNodes.forEach((node) => {
      if (node === cursor) {
        cursor = cursor.nextElementSibling;

        return;
      }

      grid.insertBefore(node, cursor);
    });

    this.lastMode = this.getMode();
    this.lastColumns = this.getColumnCount();

    this.syncHeightAfterImages();
    this.scheduleHeightSync();
    this.autoplayVideos();
  }

  scheduleHeightSync() {
    if (this.heightSyncFrame) {
      cancelAnimationFrame(this.heightSyncFrame);
    }

    this.heightSyncFrame = requestAnimationFrame(() => {
      this.heightSyncFrame = requestAnimationFrame(() => {
        this.syncInterstitialHeights();
        this.heightSyncFrame = null;
      });
    });
  }

  getProductMediaElement(productCard) {
    return (
      productCard.querySelector("[data-product-card-media]") ||
      productCard.querySelector(".product-card__media") ||
      productCard.querySelector(".product-card__image-container") ||
      productCard.querySelector(".product-card__image") ||
      productCard.querySelector("picture")
    );
  }

  syncInterstitialHeights(isLive = false) {
    const grid = this.getGrid();

    if (!grid) {
      return;
    }

    const banners = Array.from(grid.querySelectorAll(":scope > [data-plp-interstitial-rendered]"));

    banners.forEach((banner) => {
      const row = banner.dataset.interstitialRow;

      if (!row) {
        return;
      }

      const rowProducts = Array.from(
        grid.querySelectorAll(`:scope > [data-product-card][data-plp-visual-row="${row}"]`),
      ).filter((card) => {
        return !card.hidden && card.style.display !== "none" && !card.hasAttribute("data-pending-reveal");
      });

      if (!rowProducts.length) {
        this.scheduleHeightSync();
        return;
      }

      /*
       * IMPORTANT:
       * Width does NOT affect height.
       *
       * width 1 -> height of one product card
       * width 2 -> height of one product card
       */
      const productCard = rowProducts[0];

      const interstitial = banner.querySelector("[data-plp-interstitial-card]");

      if (!interstitial) {
        return;
      }

      const isOverlay = interstitial.classList.contains("plp-interstitial--overlay");

      const isBelow = interstitial.classList.contains("plp-interstitial--below");

      if (isOverlay) {
        /*
         * Overlay banner:
         * Match the COMPLETE product card, since the overlay text sits on top
         * of the image rather than adding its own height below it. The card's
         * title/price text block wraps in discrete jumps as the width crosses
         * certain thresholds — not a continuous function of width like the
         * image is — and since this always trails the browser's own reflow by
         * one frame, chasing that jump directly on every live-resize tick
         * read as a visible pop instead of a smooth scale.
         *
         * So during an active drag, only the image's height is remeasured
         * every frame; the text block's height is reused from the last
         * settled measurement (cached below) instead of being remeasured (and
         * so re-triggering that pop) on every tick. It only gets a couple of
         * pixels stale mid-drag, which is imperceptible next to a full jump,
         * and gets corrected for real the moment the drag settles.
         */
        const productMedia = this.getProductMediaElement(productCard);

        const mediaHeight = productMedia?.getBoundingClientRect().height || 0;

        if (mediaHeight <= 0) {
          this.scheduleHeightSync();
          return;
        }

        if (isLive) {
          const cachedDetailsHeight = Number(banner.dataset.overlayDetailsHeight);

          const detailsHeight = Number.isFinite(cachedDetailsHeight) ? cachedDetailsHeight : 0;

          banner.style.setProperty("--plp-interstitial-height", `${Math.ceil(mediaHeight + detailsHeight)}px`);

          return;
        }

        const cardHeight = productCard.getBoundingClientRect().height;

        if (cardHeight <= 0) {
          this.scheduleHeightSync();
          return;
        }

        banner.dataset.overlayDetailsHeight = String(Math.max(0, cardHeight - mediaHeight));

        banner.style.setProperty("--plp-interstitial-height", `${Math.ceil(cardHeight)}px`);

        return;
      }

      if (isBelow) {
        /*
         * Below-text banner:
         * Match banner MEDIA to product MEDIA, so the images line up edge to
         * edge across the row — the banner's own headline/copy block is
         * usually a different height than the product's title/price/swatches
         * block below it, so matching the *total* card height instead would
         * pull the images themselves out of alignment with each other.
         */
        const productMedia = this.getProductMediaElement(productCard);

        const mediaHeight = productMedia?.getBoundingClientRect().height || 0;

        if (mediaHeight <= 0) {
          this.scheduleHeightSync();
          return;
        }

        banner.style.setProperty("--plp-interstitial-media-height", `${Math.ceil(mediaHeight)}px`);
      }
    });
  }

  syncHeightAfterImages() {
    const grid = this.getGrid();

    if (!grid) {
      return;
    }

    const images = grid.querySelectorAll(":scope > [data-product-card] img");

    images.forEach((image) => {
      if (image.complete && image.naturalWidth > 0) {
        return;
      }

      image.addEventListener(
        "load",
        () => {
          this.scheduleHeightSync();
        },
        {
          once: true,
        },
      );

      image.addEventListener(
        "error",
        () => {
          this.scheduleHeightSync();
        },
        {
          once: true,
        },
      );
    });
  }

  autoplayVideos() {
    const grid = this.getGrid();

    if (!grid) {
      return;
    }

    grid.querySelectorAll("[data-plp-interstitial-rendered] video").forEach((video) => {
      video.muted = true;
      video.playsInline = true;

      const promise = video.play();

      if (promise && typeof promise.catch === "function") {
        promise.catch(() => {});
      }
    });
  }

  handleResize() {
    // Track the banner heights on every available frame WHILE the window is
    // actively being dragged, so they scale in step with the product cards
    // (which resize fluidly via CSS) instead of freezing until the drag
    // settles. A single-flight rAF throttle — not the double-rAF used by
    // scheduleHeightSync() to wait for the next paint after a DOM rebuild —
    // since that pattern cancels and reschedules itself on every resize tick
    // and, chased by a fast/continuous drag, can end up never actually
    // running until the drag pauses, which is what produced the "snaps into
    // place instead of flowing" feel.
    if (!this.liveResizeFrame) {
      this.liveResizeFrame = requestAnimationFrame(() => {
        this.liveResizeFrame = null;

        this.syncInterstitialHeights(true);
      });
    }

    clearTimeout(this.resizeTimer);

    this.resizeTimer = setTimeout(() => {
      // Mobile browsers fire resize repeatedly for things that don't change the
      // layout (address bar show/hide, virtual keyboard, momentum scroll). Only
      // rebuild the interstitials' DOM placement when the breakpoint mode or
      // column count actually changed, otherwise every one of those tears down
      // and re-clones every banner (restarting autoplaying videos) for no
      // visual difference — and can momentarily leave the grid without its
      // interstitials mid-rebuild.
      if (this.getMode() === this.lastMode && this.getColumnCount() === this.lastColumns) {
        // One more sync once the drag has settled, in case the last live-resize
        // frame landed mid-reflow and read a transitional size.
        this.scheduleHeightSync();

        return;
      }

      this.render();
    }, 150);
  }

  handleClick(event) {
    const control = event.target.closest("[data-plp-video-control]");

    if (!control) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const card = control.closest("[data-plp-interstitial-card]");

    const video = card?.querySelector("video");

    if (!video) {
      return;
    }

    const pauseIcon = control.querySelector("[data-video-pause]");

    const playIcon = control.querySelector("[data-video-play]");

    if (video.paused) {
      const promise = video.play();

      if (promise && typeof promise.catch === "function") {
        promise.catch(() => {});
      }

      control.setAttribute("aria-label", "Pause video");

      if (pauseIcon) {
        pauseIcon.hidden = false;
      }

      if (playIcon) {
        playIcon.hidden = true;
      }

      return;
    }

    video.pause();

    control.setAttribute("aria-label", "Play video");

    if (pauseIcon) {
      pauseIcon.hidden = true;
    }

    if (playIcon) {
      playIcon.hidden = false;
    }
  }
}
